import React, { useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Canvas } from "../../../components/marketing/journey/new-jouney/canvas";
import { Header } from "../../../components/marketing/journey/new-jouney/header";
import { PropertiesPanel } from "../../../components/marketing/journey/new-jouney/properties-panel";
import { Node } from "../types";
import { NodeConfigModal } from "../../../components/marketing/journey/new-jouney/config-modal";
import { NodePalette } from "../../../components/marketing/journey/new-jouney/node-palletes";


const JourneyBuilder: React.FC = () => {
  const [isNodePaletteCollapsed, setIsNodePaletteCollapsed] = useState(true);
  const [pendingNodePosition, setPendingNodePosition] = useState<{ index: number, type: 'between' | 'end' } | null>(null);
  const [isPropertiesPanelCollapsed, setIsPropertiesPanelCollapsed] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedNodeForConfig, setSelectedNodeForConfig] = useState<Node | null>(null);
  const [nodes, setNodes] = useState<Node[]>([
    {
      id: "1",
      type: "trigger",
      label: "Trigger",
      position: { x: 400, y: 100 },
      data: { triggerType: "schedule" },
    },
  ]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>("1");
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const lastDropY = useRef<number>(100);

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, type: string) => {
      e.dataTransfer.setData("nodeType", type);
      e.dataTransfer.effectAllowed = "move";
    },
    []
  );

  const handleAddNode = useCallback((type: string, afterNodeId?: string) => {
    let yPosition = lastDropY.current + 120;

    if (afterNodeId) {
      const afterNode = nodes.find(n => n.id === afterNodeId);
      if (afterNode) {
        yPosition = afterNode.position.y + 120;
      }
    }

    const newNode: Node = {
      id: Date.now().toString(),
      type: type as Node["type"],
      label: type.charAt(0).toUpperCase() + type.slice(1),
      position: { x: 400, y: yPosition },
    };

    setNodes((prev) => {
      if (afterNodeId) {
        const index = prev.findIndex(n => n.id === afterNodeId);
        if (index !== -1) {
          const newNodes = [...prev];
          newNodes.splice(index + 1, 0, newNode);
          return newNodes;
        }
      }
      return [...prev, newNode];
    });

    setSelectedNodeId(newNode.id);
    lastDropY.current = yPosition;
    toast.success(`Added ${newNode.label} node`);
  }, [nodes]);

  const handleDropNode = useCallback((type: string, x: number, y: number) => {
    handleAddNode(type);
  }, [handleAddNode]);

  // Other handlers remain similar but updated for new props...
  const handleSelectNode = useCallback((id: string) => {
    setSelectedNodeId(id);
  }, []);

  const handleDragNode = useCallback(
    (e: React.MouseEvent, nodeId: string) => {
      e.preventDefault();
      setIsDragging(true);

      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        setDragOffset({
          x: e.clientX - node.position.x,
          y: e.clientY - node.position.y,
        });
      }

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDragging) return;

        setNodes((prev) =>
          prev.map((node) =>
            node.id === nodeId
              ? {
                ...node,
                position: {
                  x: moveEvent.clientX - dragOffset.x,
                  y: moveEvent.clientY - dragOffset.y,
                },
              }
              : node
          )
        );
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [nodes, dragOffset, isDragging]
  );

  const handleUpdateNode = useCallback((nodeId: string, data: any) => {
    setNodes((prev) =>
      prev.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
      )
    );
  }, []);

  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      if (nodes.length <= 1) {
        toast.error("You need at least one node in the journey");
        return;
      }

      const nodeToDelete = nodes.find((n) => n.id === nodeId);
      setNodes((prev) => prev.filter((node) => node.id !== nodeId));

      if (selectedNodeId === nodeId) {
        const otherNodes = nodes.filter((n) => n.id !== nodeId);
        setSelectedNodeId(otherNodes.length > 0 ? otherNodes[0].id : null);
      }

      if (nodeToDelete) {
        toast.success(`Deleted ${nodeToDelete.label} node`);
      }
    },
    [nodes, selectedNodeId]
  );

  const handleSave = useCallback(() => {
    toast.success("Journey saved successfully!");
  }, [nodes]);

  const handleExport = useCallback(() => {
    const dataStr = JSON.stringify(nodes, null, 2);
    const dataUri = "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const exportFileDefaultName = "journey.json";

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();

    toast.success("Journey exported successfully!");
  }, [nodes]);

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) || null;

  const handleToggleNodePalette = useCallback(() => {
    setIsNodePaletteCollapsed(!isNodePaletteCollapsed);
  }, [isNodePaletteCollapsed]);

  const handleTogglePropertiesPanel = useCallback(() => {
    setIsPropertiesPanelCollapsed(!isPropertiesPanelCollapsed);
  }, [isPropertiesPanelCollapsed]);

  const handleNodeTypeSelect = useCallback((type: string) => {
    if (pendingNodePosition) {
      if (pendingNodePosition.type === 'between') {
        handleAddNode(type, nodes[pendingNodePosition.index].id);
      } else {
        handleAddNode(type);
      }
      setPendingNodePosition(null);
    } else {
      handleAddNode(type);
    }
  }, [pendingNodePosition, handleAddNode, nodes]);

  const handleOpenConfigModal = useCallback((node: Node) => {
    setSelectedNodeForConfig(node);
    setConfigModalOpen(true);
  }, []);

  const handleCloseConfigModal = useCallback(() => {
    setConfigModalOpen(false);
    setSelectedNodeForConfig(null);
  }, []);

  const handleSaveNodeConfig = useCallback((nodeId: string, data: any) => {
    setNodes((prev) =>
      prev.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
      )
    );
    toast.success("Node configuration saved!");
  }, []);

  const handleNodeClick = useCallback((nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setSelectedNodeId(nodeId);
      handleOpenConfigModal(node);
    }
  }, [nodes, handleOpenConfigModal]);

  return (
    <div className="h-screen bg-white flex flex-col">
      {/* <ToastContainer position="top-right" autoClose={3000} /> */}
      <Header onSave={handleSave} onExport={handleExport} />
      <div className="flex-1 flex">
        <NodePalette
          onDragStart={handleDragStart}
          onAddNode={handleAddNode}
          onNodeTypeSelect={handleNodeTypeSelect}
          isCollapsed={isNodePaletteCollapsed}
          onToggleCollapse={handleToggleNodePalette}
        />
        <Canvas
          nodes={nodes}
          selectedNodeId={selectedNodeId}
          onSelectNode={handleSelectNode} // This selects the node
          onNodeClick={(nodeId) => {
            const node = nodes.find(n => n.id === nodeId);
            if (node) handleOpenConfigModal(node); // Pass full Node
          }}
          onDropNode={handleDropNode}
          onDragNode={handleDragNode}
          onDeleteNode={handleDeleteNode}
          onAddNode={handleAddNode}
          onToggleNodePalette={handleToggleNodePalette}
          isNodePaletteCollapsed={isNodePaletteCollapsed}
        />
        <PropertiesPanel
          selectedNode={selectedNode}
          onUpdateNode={handleUpdateNode}
          onDeleteNode={handleDeleteNode}
          isCollapsed={isPropertiesPanelCollapsed}
          onToggleCollapse={handleTogglePropertiesPanel}

        />

        <NodeConfigModal
          node={selectedNodeForConfig}
          isOpen={configModalOpen}
          onClose={handleCloseConfigModal}
          onSave={handleSaveNodeConfig}
        />
      </div>
    </div>
  );
};

export default JourneyBuilder;